import { Component, Inject, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, Validators, ReactiveFormsModule } from '@angular/forms';
import { forkJoin, Subscription } from 'rxjs';
import { ProjectService } from '../../../services/project.service';
import { ProfileService } from '../../../services/profile.service';
import { Project } from '../../../dtos/project';
import { UserInfo } from '../../../dtos/user-info';
import {
  ExtractionQuestion,
  QuestionType,
} from '../../../extraction-schema/domain/extraction-question.model';
import {
  validateQuestionSchema,
  reorderQuestions,
} from '../../../extraction-schema/domain/extraction-schema.core';
import {
  QUESTIONNAIRE_PORT,
  QuestionnairePort,
} from '../../../extraction-schema/ports/questionnaire.port';
import { HttpQuestionnaireAdapter } from '../../../extraction-schema/adapters/http-questionnaire.adapter';
import { ExtractionQuestionCardComponent } from '../extraction-question-card/extraction-question-card.component';
import { ConfirmModalComponent } from '../../modals/confirm-modal/confirm-modal.component';
import { BaseModalComponent } from '../../modals/base-modal/base-modal.component';

@Component({
  selector: 'app-project-extraction-questions',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ExtractionQuestionCardComponent,
    ConfirmModalComponent,
    BaseModalComponent,
  ],
  providers: [{ provide: QUESTIONNAIRE_PORT, useClass: HttpQuestionnaireAdapter }],
  templateUrl: './project-extraction-questions.component.html',
  styleUrls: ['./project-extraction-questions.component.scss'],
})
export class ProjectExtractionQuestionsComponent implements OnDestroy {
  readonly QuestionType = QuestionType;

  @Input() projectId = '';
  @Output() openChange = new EventEmitter<boolean>();

  private _open = false;
  @Input() set open(value: boolean) {
    const prev = this._open;
    this._open = value;
    if (value && !prev && this.projectId) {
      this.resetState();
      // Defer data loading so observable subscriptions fire outside the
      // current change-detection cycle, avoiding ExpressionChanged errors.
      setTimeout(() => this.loadData(), 0);
    }
  }
  get open(): boolean {
    return this._open;
  }

  project: Project | null = null;
  currentUser: UserInfo | null = null;
  loading = true;
  saving = false;

  // Custom alerts state
  errorMessage: string | null = null;
  successMessage: string | null = null;
  validationErrors: string[] = [];

  // Questionnaire state
  questionsForm!: FormGroup;
  isEditable = false;
  isLocked = false;
  draggedIndex: number | null = null;
  showDeleteConfirm = false;
  questionIndexPendingDelete: number | null = null;

  // Modal + unsaved-changes guard
  showDiscardConfirm = false;
  /**
   * Normalized snapshot of the last persisted questionnaire. The backend may omit empty
   * `options`, while the form always exposes them as an array; normalizing prevents a saved form
   * from being treated as dirty.
   */
  private savedSnapshot = '[]';

  readonly QUESTION_TYPES = [
    { value: QuestionType.FULL_TEXT, label: 'Text Field' },
    { value: QuestionType.NUMERIC, label: 'Numeric Value' },
    { value: QuestionType.BOOLEAN, label: 'Yes / No' },
    { value: QuestionType.SINGLE_CHOICE, label: 'Single Choice' },
    { value: QuestionType.MULTI_CHOICE, label: 'Multiple Choice' },
  ];

  private loadSub?: Subscription;

  constructor(
    private fb: FormBuilder,
    private projectService: ProjectService,
    private profileService: ProfileService,
    @Inject(QUESTIONNAIRE_PORT) private questionnairePort: QuestionnairePort,
  ) {
    this.initForm();
  }

  private resetState(): void {
    this.loading = true;
    this.saving = false;
    this.errorMessage = null;
    this.successMessage = null;
    this.validationErrors = [];
    this.showDeleteConfirm = false;
    this.questionIndexPendingDelete = null;
    this.showDiscardConfirm = false;
    this.savedSnapshot = '[]';
    this.initForm();
  }

  private loadData(): void {
    this.loadSub?.unsubscribe();
    this.loadSub = forkJoin({
      user: this.profileService.getCurrentUser(),
      project: this.projectService.getProjectById(this.projectId),
    }).subscribe({
      next: (bundle) => {
        this.currentUser = bundle.user;
        this.project = bundle.project;
        const isPI = String(bundle.project.piUserId) === String(bundle.user.id);
        const stateLower = bundle.project.state?.toLowerCase();
        const isEditableState = stateLower !== 'data_extraction' && stateLower !== 'completed';
        this.isEditable = isPI && isEditableState;
        this.isLocked = !isEditableState;
        this.loadQuestions();
      },
      error: (err) => {
        console.error('Error loading project:', err);
        this.errorMessage = 'Could not load project information.';
        this.loading = false;
      },
    });
  }

  ngOnDestroy(): void {
    this.loadSub?.unsubscribe();
  }

  private initForm(): void {
    this.questionsForm = this.fb.group({
      questions: this.fb.array([]),
    });
  }

  get questionsFormArray(): FormArray {
    return this.questionsForm.get('questions') as FormArray;
  }

  getQuestionFormGroup(index: number): FormGroup {
    return this.questionsFormArray.at(index) as FormGroup;
  }

  getOptionsFormArray(questionIndex: number): FormArray {
    return this.getQuestionFormGroup(questionIndex).get('options') as FormArray;
  }

  private loadQuestions(): void {
    this.questionnairePort.getQuestions(this.projectId).subscribe({
      next: (questions) => {
        this.populateForm(questions);
        this.savedSnapshot = this.createQuestionsSnapshot(questions);
        this.snapshotFormValue();
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading questions:', err);
        this.errorMessage = 'Could not load extraction questions.';
        this.loading = false;
      },
    });
  }

  private populateForm(questions: ExtractionQuestion[]): void {
    this.questionsFormArray.clear();

    // Sort just in case backend index sequence is offset
    const sorted = [...questions].sort((a, b) => a.orderIndex - b.orderIndex);

    sorted.forEach((q) => {
      this.questionsFormArray.push(this.createQuestionFormGroup(q));
    });
  }

  private createQuestionFormGroup(q: Partial<ExtractionQuestion>): FormGroup {
    const optionsArray = this.fb.array([]);
    if (q.options) {
      q.options.forEach((opt) => {
        optionsArray.push(this.fb.control(opt, Validators.required));
      });
    }

    return this.fb.group({
      id: [q.id],
      type: [q.type || QuestionType.FULL_TEXT, Validators.required],
      text: [q.text || '', [Validators.required, Validators.maxLength(500)]],
      required: [q.required ?? true],
      orderIndex: [q.orderIndex ?? 0],
      options: optionsArray,
    });
  }

  addQuestion(): void {
    if (!this.isEditable) return;

    this.validationErrors = [];
    const newQuestion: Partial<ExtractionQuestion> = {
      type: QuestionType.FULL_TEXT,
      text: '',
      required: true,
      orderIndex: this.questionsFormArray.length,
      options: [],
    };

    this.questionsFormArray.push(this.createQuestionFormGroup(newQuestion));
  }

  askRemoveQuestion(index: number): void {
    if (!this.isEditable) return;
    this.questionIndexPendingDelete = index;
    this.showDeleteConfirm = true;
  }

  cancelRemoveQuestion(): void {
    this.showDeleteConfirm = false;
    this.questionIndexPendingDelete = null;
  }

  confirmRemoveQuestion(): void {
    if (this.questionIndexPendingDelete === null) return;
    this.validationErrors = [];
    this.questionsFormArray.removeAt(this.questionIndexPendingDelete);
    this.reindexQuestions();
    this.showDeleteConfirm = false;
    this.questionIndexPendingDelete = null;
  }

  changeQuestionType(index: number, newType: QuestionType): void {
    if (!this.isEditable) return;

    const questionGroup = this.getQuestionFormGroup(index);
    questionGroup.patchValue({ type: newType });

    const optionsArray = this.getOptionsFormArray(index);
    const isChoice =
      newType === QuestionType.SINGLE_CHOICE || newType === QuestionType.MULTI_CHOICE;

    if (isChoice) {
      // Initialize with at least one option if empty
      if (optionsArray.length === 0) {
        optionsArray.push(this.fb.control('', Validators.required));
      }
    } else {
      // Clear options for non-choice types
      optionsArray.clear();
    }
  }

  addOption(questionIndex: number): void {
    if (!this.isEditable) return;
    this.getOptionsFormArray(questionIndex).push(this.fb.control('', Validators.required));
  }

  removeOption(questionIndex: number, optionIndex: number): void {
    if (!this.isEditable) return;
    this.getOptionsFormArray(questionIndex).removeAt(optionIndex);
  }

  private reindexQuestions(): void {
    this.questionsFormArray.controls.forEach((control, idx) => {
      control.patchValue({ orderIndex: idx });
    });
  }

  // --- Dynamic HTML5 Drag-and-Drop Reordering ---

  onDragStart(event: DragEvent, index: number): void {
    if (!this.isEditable) return;
    this.draggedIndex = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', String(index));
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onDrop(event: DragEvent, targetIndex: number): void {
    event.preventDefault();
    if (!this.isEditable || this.draggedIndex === null || this.draggedIndex === targetIndex) {
      return;
    }

    const currentQuestions = this.getQuestionsFromForm();
    const reordered = reorderQuestions(currentQuestions, this.draggedIndex, targetIndex);

    // Re-populate form arrays to sync UI
    this.populateForm(reordered);
    this.draggedIndex = null;
  }

  // --- Form Mapping & Saving ---

  private getQuestionsFromForm(): ExtractionQuestion[] {
    return this.questionsFormArray.controls.map((control) => {
      const value = control.value;
      return {
        id: value.id || undefined,
        type: value.type,
        text: value.text,
        required: value.required,
        orderIndex: value.orderIndex,
        options: value.options && value.options.length > 0 ? value.options : undefined,
      };
    });
  }

  saveQuestions(): void {
    if (!this.isEditable) return;

    this.errorMessage = null;
    this.successMessage = null;
    this.validationErrors = [];

    const questions = this.getQuestionsFromForm();

    // Core Schema Hexagonal Validation check
    const errors = validateQuestionSchema(questions);
    if (errors.length > 0) {
      this.validationErrors = errors;
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    this.saving = true;
    this.questionnairePort.saveQuestions(this.projectId, questions).subscribe({
      next: (savedQuestions) => {
        this.populateForm(savedQuestions);
        this.savedSnapshot = this.createQuestionsSnapshot(savedQuestions);
        // Reset the "has changed" marker by re-snapshotting the form's raw value. We compare
        // against the form's raw `questionsFormArray.value` in hasUnsavedChanges(), so the
        // snapshot must use the same shape — anything else (e.g. getQuestionsFromForm()
        // output, the API response) risks a false-positive dirty state due to mismatches like
        // `options: []` vs `options: undefined` or `id: null` vs `id: undefined`.
        this.snapshotFormValue();
        this.successMessage = 'Questionnaire schema saved successfully!';
        this.saving = false;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
      error: (err) => {
        console.error('Error saving questionnaire schema:', err);
        // Extract backend custom exception messages if present
        this.errorMessage =
          err.error?.message ||
          err.error ||
          'Failed to save questionnaire schema. Please check authorization or active extractions status.';
        this.saving = false;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
    });
  }

  /**
   * Snapshot the form's current raw value into `savedSnapshot` so it can be compared against
   * `questionsFormArray.value` in hasUnsavedChanges(). Uses the form's raw value (not the
   * API response or getQuestionsFromForm() output) to guarantee identical shape on both
   * sides of the JSON.stringify comparison.
   */
  private snapshotFormValue(): void {
    this.savedSnapshot = this.createQuestionsSnapshot(
      this.questionsFormArray.getRawValue() as Partial<ExtractionQuestion>[],
    );
  }

  /**
   * True if the form currently holds values that differ from the last persisted snapshot. Used
   * to gate the discard-changes confirm popup when the user attempts to close the modal.
   */
  hasUnsavedChanges(): boolean {
    if (this.saving || this.loading) {
      return false;
    }
    return this.createQuestionsSnapshot(this.getQuestionsFromForm()) !== this.savedSnapshot;
  }

  private createQuestionsSnapshot(questions: Partial<ExtractionQuestion>[]): string {
    const normalized = questions.map((question) => ({
      id: question.id ?? null,
      type: question.type ?? QuestionType.FULL_TEXT,
      text: question.text ?? '',
      required: question.required ?? true,
      orderIndex: question.orderIndex ?? 0,
      options: question.options ?? [],
    }));

    return JSON.stringify(normalized);
  }

  /**
   * Cancellable close hook for the base-modal. When the user attempts to close (X button,
   * backdrop, escape) and the form has unsaved changes, cancel the close and show the
   * discard-confirmation popup instead. Otherwise let the close proceed normally (which will
   * unmount the route and navigate to the project root via `(openChange)="onClose()"`).
   */
  onBeforeClose(event: { reason: 'button' | 'backdrop' | 'escape'; cancel: () => void }): void {
    if (this.hasUnsavedChanges()) {
      event.cancel();
      this.showDiscardConfirm = true;
    }
  }

  /** User confirmed they want to discard unsaved changes — close the modal. */
  confirmDiscard(): void {
    this.showDiscardConfirm = false;
    this.onClose();
  }

  /** User chose to keep editing — just hide the discard confirm, modal stays open. */
  cancelDiscard(): void {
    this.showDiscardConfirm = false;
  }

  /**
   * Close and return to the previous page.
   */
  onClose(): void {
    this.openChange.emit(false);
  }
}
